import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle2, Phone, ChevronDown, Zap } from "lucide-react";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Kitchen & Bathroom Renovations",
  "New Builds — Homes, Granny Flats & Sheds",
  "PowerPoints, RCDs, Smoke Alarms & Downlights",
  "Switchboard Upgrades & Surge Protection",
  "Residential & Commercial Maintenance",
  "Safety Certificates",
  "Something else",
];

const inputCls =
  "w-full rounded-none border-2 border-ink/20 bg-white px-4 py-3.5 text-base text-ink placeholder:text-ink/30 transition-colors focus:border-ink focus:outline-none";

const labelCls = "mb-2 block text-xs uppercase tracking-[0.25em] text-ink/50";

const Quote = () => {
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get("service") || "";
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICES.includes(prefill) ? prefill : "",
    details: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        service: form.service,
        details: form.details,
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong sending that through. Try again — or just call Derek on 0490 425 408.");
    }
  };

  return (
    <main className="bg-[#f7f8f6] pt-24 md:pt-32" data-testid="quote-page">
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pb-20 md:px-10 md:pb-28 lg:grid-cols-2 lg:gap-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-petrol md:text-sm" data-testid="quote-overline">
            <Zap className="h-3.5 w-3.5 animate-spark-flicker text-copper" strokeWidth={2} fill="currentColor" />
            Get a quote
          </p>
          <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-petrol md:text-7xl">
            Let's talk
            <br />
            specs<span className="text-copper">.</span>
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-ink/60 md:text-lg">
            Tell us what you're planning and we'll get back to you ASAP — with a
            straight answer and a fair price.
          </p>
          <div className="mt-12 border-2 border-ink bg-white p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/40">Rather talk it through?</p>
            <a
              href="tel:0490425408"
              data-testid="quote-call-link"
              className="mt-3 flex items-center gap-3 font-display text-2xl font-bold tracking-tight text-petrol transition-colors hover:text-ink"
            >
              <Phone className="h-5 w-5 text-petrol" strokeWidth={1.5} />
              0490 425 408
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          {status === "done" ? (
            <div className="flex h-full flex-col items-start justify-center border-2 border-ink bg-white p-10 shadow-[8px_8px_0_#c8925a]" data-testid="quote-success-panel">
              <CheckCircle2 className="h-10 w-10 text-petrol" strokeWidth={1.25} />
              <h2 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight text-petrol">
                Request received.
              </h2>
              <p className="mt-4 max-w-sm text-base leading-relaxed text-ink/60">
                Derek will be in touch shortly. Need it sooner? Call{" "}
                <a href="tel:0490425408" className="font-medium text-petrol hover:text-ink" data-testid="quote-success-call-link">
                  0490 425 408
                </a>
                .
              </p>
              <Link
                to="/"
                data-testid="quote-success-home-link"
                className="mt-8 text-xs uppercase tracking-[0.25em] text-petrol transition-colors hover:text-ink"
              >
                Back to site
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="border-2 border-ink bg-white p-6 shadow-[8px_8px_0_#c8925a] md:p-10" data-testid="quote-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="q-name" className={labelCls}>Name *</label>
                  <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-phone" className={labelCls}>Phone *</label>
                    <input id="q-phone" data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="q-email" className={labelCls}>Email</label>
                    <input id="q-email" data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Optional" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="q-service" className={labelCls}>Service *</label>
                  <div className="relative">
                    <select
                      id="q-service"
                      data-testid="quote-service-select"
                      required
                      value={form.service}
                      onChange={set("service")}
                      className={`${inputCls} appearance-none pr-10 ${form.service ? "" : "text-ink/30"}`}
                    >
                      <option value="" disabled>What do you need done?</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s} className="bg-white text-ink">{s}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" strokeWidth={1.5} />
                  </div>
                </div>
                <div>
                  <label htmlFor="q-details" className={labelCls}>Job details</label>
                  <textarea
                    id="q-details"
                    data-testid="quote-details-textarea"
                    rows={4}
                    value={form.details}
                    onChange={set("details")}
                    placeholder="What are you planning? Sizes, timing, anything that helps."
                    className={`${inputCls} resize-none`}
                  />
                </div>
                {error && (
                  <p className="border-2 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="quote-error-message">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={status === "sending"}
                  className="group flex w-full items-center justify-center gap-2 bg-petrol px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-copper hover:text-ink disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Request Quote"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </section>
      <Footer />
    </main>
  );
};

export default Quote;
